import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Users,
  Award,
  Clock,
  Heart,
  AlertTriangle
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../ui/accordion";

export default function EmergencyTraumaPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = [];


  const subspecialties = [
    {
      title: "Accident & Polytrauma Care",
      description: "Immediate stabilization and injury management.",
      image: "/Accident&PolytraumaCare.jpg",
    },
    {
      title: "Cardiac Emergencies",
      description: "Heart attack, arrhythmias & chest pain evaluation.",
      image: "/CardiacEmergencies.jpg",
    },
    {
      title: "Stroke & Neurological Emergencies",
      description: "Rapid assessment & thrombolysis support.",
      image: "/Stroke&NeurologicalEmergencies.jpg",
    },
    {
      title: "Pediatric Emergencies",
      description: "Fever, seizures, breathing difficulty & dehydration.",
      image: "/PediatricEmergencies.jpg",
    },
    {
      title: "Surgical Emergencies",
      description: "Appendicitis, abdominal pain, fractures & bleeding.",
      image: "/SurgicalEmergencies.jpg",
    },
    {
      title: "Respiratory Emergencies",
      description: "Asthma, COPD flare-ups & breathing issues.",
      image: "/RespiratoryEmergencies.jpg",
    },
    {
      title: "Poisoning & Toxicology Care",
      description: "Overdose and poisoning management.",
      image: "/Poisoning&ToxicologyCare.jpg",
    },
  ];

  const treatments = [
    "24x7 Advanced Trauma & Emergency Response",
    "Cardiac & Stroke Emergency Management",
    "Immediate Fracture & Trauma Stabilization",
    "Emergency CT, X-ray & Ultrasound Imaging",
    "Emergency Ventilation & Critical Care Support",
    "Minor Procedure Room (Suturing, Wound Care)",
    "Poisoning & Drug Overdose Management",
    "Rapid Laboratory & Blood Bank Support",
    "Ambulance Support with Trained Paramedics",
  ];

  const whyChoose = [
    {
      icon: Clock,
      title: "24x7 Emergency Department",
      description: "24x7 emergency department with rapid response",
    },
    {
      icon: Users,
      title: "Expert Medical Team",
      description: "Experienced emergency physicians, trauma surgeons & critical care specialists",
    },
    {
      icon: Award,
      title: "On-Site Diagnostics",
      description: "On-site imaging (CT, X-ray), labs & blood bank for immediate diagnosis",
    },
    {
      icon: Heart,
      title: "Dedicated Trauma Bays",
      description: "Dedicated trauma bays & resuscitation rooms",
    },
    {
      icon: CheckCircle2,
      title: "ICU Support",
      description: "ICU support for critically ill patients with ambulance services",
    },
  ];

  const articles = [
    {
      title: "What to Do Immediately After a Road Accident",
      excerpt:
        "Essential steps to take after a road accident to ensure safety and proper medical attention for all involved.",
    },
    {
      title: "Recognizing a Heart Attack — The Golden Hour Explained",
      excerpt:
        "Understanding heart attack symptoms and why immediate medical intervention within the golden hour is crucial.",
    },
    {
      title: "Stroke Emergency: Why Every Minute Matters",
      excerpt:
        "Learn to recognize stroke symptoms and understand the importance of rapid response in stroke treatment.",
    },
    {
      title: "How to Handle Common Home Emergencies Safely",
      excerpt:
        "A practical guide to managing common household emergencies before professional help arrives.",
    },
  ];

  const faqs = [
    {
      question: "Is your Emergency Department available 24x7?",
      answer:
        "Yes, our emergency and trauma team is available round-the-clock, 24 hours a day, 7 days a week for all critical situations.",
    },
    {
      question: "Do you handle accident and trauma cases?",
      answer:
        "Absolutely — we have a dedicated trauma team fully equipped to manage all types of accidents with immediate stabilization.",
    },
    {
      question: "Do you offer ambulance services?",
      answer:
        "Yes, we provide 24x7 ambulance support with trained paramedics and advanced life support systems.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "EmergencyService",
    "name": "MAMS Hospitals Emergency & Trauma Care",
    "url": "https://mamshospitals.com/emergency-trauma",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/Emergency&Trauma.jpg",
    "description": "MAMS Hospitals provides 24x7 emergency and trauma care in Hyderabad including accident care, cardiac emergencies, stroke management, ICU support, ambulance services, and critical care.",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "24x7 Trauma & Emergency Care"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Accident & Polytrauma Care"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Stroke Emergency Management"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Cardiac Emergency Care"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Emergency ICU & Ventilator Support"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is emergency care available 24x7?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides round-the-clock 24x7 emergency and trauma care services."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide ambulance services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals offers ambulance services with trained paramedics and emergency life support."
        }
      },
      {
        "@type": "Question",
        "name": "Do you handle accident and trauma cases?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals has a dedicated trauma team for accidents, fractures, bleeding injuries, and critical emergencies."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Emergency & Trauma Care - MAMS Hospitals",
    "url": "https://mamshospitals.com/emergency-trauma",
    "description": "Best emergency and trauma care hospital in Hyderabad offering 24x7 accident care, cardiac emergency treatment, stroke management, ICU support, ambulance services, and critical care.",
    "keywords": [
      "Emergency Hospital Hyderabad",
      "24x7 Emergency Care Hyderabad",
      "Trauma Care Hospital Hyderabad",
      "Accident Care Hyderabad",
      "Critical Care Hyderabad",
      "Ambulance Service Hyderabad",
      "Emergency Hospital Bachupally"
    ]
  }
];

  return (
    <>
    <SEO
      title="Best Emergency & Trauma Care Hospital in Hyderabad | 24x7 Emergency Care | MAMS Hospitals"
      description="MAMS Hospitals offers 24x7 emergency and trauma care in Hyderabad including accident care, stroke management, cardiac emergencies, ICU support, ambulance services, and critical care."
      keywords="emergency hospital Hyderabad,
    24x7 emergency care Hyderabad,
    trauma care hospital Hyderabad,
    accident care Hyderabad,
    critical care Hyderabad,
    ambulance service Hyderabad,
    stroke emergency Hyderabad,
    cardiac emergency Hyderabad,
    ICU hospital Hyderabad,
    emergency trauma center Hyderabad,
    polytrauma care Hyderabad,
    emergency hospital Bachupally,
    MAMS Hospitals Emergency Care"
      schema={schema}
    />
    <div className="flex flex-col min-h-screen">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Emergency & Trauma Care Hospital in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                When Every Second Counts — Trust MAMS Hospitals
              </h1>

              <p className="text-lg text-gray-700 mb-8">
                MAMS Hospitals is one of the Best Emergency & Trauma Care Hospitals in Hyderabad, providing 24x7 rapid-response medical and surgical care for critical and life-threatening emergencies.
              </p>

             <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl">
                <div className="text-3xl font-bold">24/7</div>
                <div className="text-sm">Emergency Care</div>
              </div>

              <img
                src="/Emergency&Trauma.jpg"
                alt="Emergency & Trauma Care at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/emergency-trauma"
      />
            </div>

          </div>

        </div>
      </section>

      {/* Sub-Specialties Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container max-w-6xl mx-auto px-4">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold">
              Sub-Specialities in Emergency & Trauma Care
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive services for all types of medical emergencies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((item, index) => (
              <Card key={index} className="group hover:shadow-xl border-gray-200 hover:border-teal-500 transition">

                <CardContent className="p-0">
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition"
                    />
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold">{item.title}</h3>
                    <p className="text-gray-600 mt-2">{item.description}</p>
                  </div>
                </CardContent>

              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* Treatments Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container max-w-6xl mx-auto px-4">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold">Treatments & Emergency Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Rapid-response treatments with advanced medical technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((item, index) => (
              <div className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm" key={index}>
                <CheckCircle2 className="h-6 w-6 text-teal-600 mt-1" />
                <span className="text-gray-700 font-medium">{item}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why Choose MAMS Hospitals for Emergency & Trauma Care
          </h2>

          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Trusted emergency care backed by expertise & technology
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow">

                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <item.icon className="h-7 w-7 text-teal-600" />
                  </div>

                  <h3 className="text-xl font-bold">{item.title}</h3>
                  <p className="text-gray-600 mt-2">{item.description}</p>
                </CardContent>

              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* Specialist Section */}
      {/* <section className="py-16 md:py-20 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Meet Our Specialist</h2>

          <p className="text-gray-600 text-center mb-12">
            Expert emergency care from experienced professionals
          </p>

          <Card className="max-w-4xl mx-auto shadow-xl border-gray-200">

            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-8 items-center">

                <div className="flex justify-center">
                  <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-teal-500">
                    <img
                      src="/dr-rajesh-kumar-emergency-specialist.jpg"
                      alt="Dr. Rajesh Kumar"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <h3 className="text-2xl font-bold mb-2">Dr. Rajesh Kumar</h3>
                  <p className="text-teal-600 font-semibold mb-4">Chief Emergency & Trauma Specialist</p>

                  <p className="text-gray-700 mb-6">
                    Expert in trauma, cardiac emergencies & stroke management with years of experience handling
                    life-threatening cases.
                  </p>

                  <Link to="tel:+917399999981">
                    <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                      <Phone className="mr-2 h-5 w-5" />
                      Emergency Call
                    </Button>
                  </Link>
                </div>

              </div>
            </CardContent>

          </Card>

          <div className="text-center mt-8">
            <Link to="/doctors" className="text-teal-600 hover:text-teal-700 font-semibold">
              View All Emergency Specialists →
            </Link>
          </div>

        </div>
      </section> */}

      {/* Articles Section */}
      {/* <section className="py-16 md:py-20 bg-white">
        <div className="container max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Recent Articles</h2>
          <p className="text-gray-600 text-center mb-12">
            Stay informed about emergency care and safety
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {articles.map((item, index) => (
              <Card key={index} className="group hover:shadow-xl border-gray-200 transition cursor-pointer">

                <CardContent className="p-6">
                  <h3 className="text-xl font-bold group-hover:text-teal-600">{item.title}</h3>
                  <p className="text-gray-600 mt-2">{item.excerpt}</p>
                </CardContent>

              </Card>
            ))}
          </div>

        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container max-w-4xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Frequently Asked Questions
          </h2>

          <p className="text-gray-600 text-center mb-12">
            Get answers to common questions about emergency care
          </p>

          <Accordion type="single" collapsible>
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
